import React from "react";
import {
  Box,
  Flex,
  HStack,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";

const TOP_BAR_BLUE = "#00005f";
const NAME_COL_WIDTH = "150px";

/** Green for points won, red for lost, neutral for a tie or nothing yet. */
function pointsColor(points) {
  if (points == null) return "gray.500";
  if (points > 0) return "green.600";
  if (points < 0) return "red.600";
  return TOP_BAR_BLUE;
}

/**
 * What trails the team name in a cell.
 *
 * Once scored it's the signed points; while a game is in progress it falls
 * back to the confidence staked, so the cell still shows what's riding on it.
 */
function resultLabel(cell) {
  const pts = cell.points_awarded;
  if (pts == null) {
    return cell.confidence_points != null ? `(${cell.confidence_points})` : "";
  }
  return pts > 0 ? `+${pts}` : `${pts}`;
}

/**
 * Every player's pick for every week: players down, weeks across.
 *
 * Only the player column and header row are sticky — the grid is wide enough
 * (38 matchweeks for the PL) that the name has to stay visible while scrolling
 * sideways, and the week numbers while scrolling down.
 *
 * `weekPrefix` is "MW" for the Premier League and "WK" for the NFL.
 */
export default function AllPicksTable({ data, loading, weekPrefix = "WK" }) {
  if (loading) {
    return <Flex justify="center" py={12}><Spinner size="lg" /></Flex>;
  }
  if (!data?.rows?.length) {
    return <Text color="gray.600">No picks to show yet.</Text>;
  }

  const weeks = data.weeks || [];

  return (
    <Box overflowX="auto" overflowY="auto" maxH={{ base: "70vh", md: "75vh" }}>
      <Table size="sm" variant="simple"
             sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
        <Thead>
          <Tr>
            <Th
              position="sticky" top={0} left={0} zIndex={4} bg="white"
              w={NAME_COL_WIDTH} minW={NAME_COL_WIDTH}
              borderBottom="2px solid" borderColor="gray.200"
            >
              Player
            </Th>
            <Th
              position="sticky" top={0} left={NAME_COL_WIDTH} zIndex={4} bg="white"
              isNumeric borderBottom="2px solid" borderColor="gray.200"
            >
              PTS
            </Th>
            {weeks.map((wk) => (
              <Th
                key={wk}
                position="sticky" top={0} zIndex={3} bg="white"
                minW="74px" textAlign="center"
                borderBottom="2px solid" borderColor="gray.200"
              >
                {weekPrefix}{wk}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {data.rows.map((row, i) => {
            const stripe = i % 2 === 0 ? "white" : "gray.50";
            return (
              <Tr key={row.user_id}>
                <Td
                  position="sticky" left={0} zIndex={2} bg={stripe}
                  w={NAME_COL_WIDTH} minW={NAME_COL_WIDTH}
                  fontWeight="medium" whiteSpace="nowrap"
                >
                  {row.username}
                </Td>
                <Td
                  position="sticky" left={NAME_COL_WIDTH} zIndex={2} bg={stripe}
                  isNumeric fontWeight="bold" color={pointsColor(row.points)}
                >
                  {row.points}
                </Td>
                {weeks.map((wk) => {
                  const cell = row.picks?.[String(wk)];
                  // No pick at all, versus a pick that hasn't been revealed —
                  // the second only happens before that game kicks off.
                  if (!cell) {
                    return (
                      <Td key={wk} bg={stripe} textAlign="center" fontSize="xs"
                          color="gray.300">
                        —
                      </Td>
                    );
                  }
                  if (!cell.revealed) {
                    return (
                      <Td key={wk} bg={stripe} textAlign="center" fontSize="xs"
                          color="gray.400">
                        ·
                      </Td>
                    );
                  }
                  return (
                    <Td
                      key={wk}
                      bg={stripe}
                      textAlign="center"
                      whiteSpace="nowrap"
                      px={1}
                      borderWidth={cell.is_auto_pick ? "2px" : undefined}
                      borderColor={cell.is_auto_pick ? "red.500" : undefined}
                      borderStyle={cell.is_auto_pick ? "solid" : undefined}
                    >
                      <HStack spacing={1} justify="center">
                        <Text fontSize="xs" fontWeight="bold" color={TOP_BAR_BLUE}>
                          {cell.team?.short_name || "—"}
                        </Text>
                        <Text fontSize="xs" fontWeight="bold"
                              color={pointsColor(cell.points_awarded)}>
                          {resultLabel(cell)}
                        </Text>
                      </HStack>
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
}
